import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { PageRoot, PageHeader, Toolbar } from '../ui/Page';
import { SearchInput } from '../ui/SearchInput';
import { DataTable, dash, type DataTableColumn } from '@/components/ui/data-table';
import { useServerTable } from '../ui/useServerTable';
import { UnderlineTabs, UnderlineTabStrip, UnderlineTabPanel } from '../ui/Tabs';
import { ASSET_TABS, useUrlTab } from '../ui/pageTabs';
import { StatusBadge } from '../ui/Badge';
import { Sheet, SheetOpenFull } from '../ui/Sheet';
import { EmptyState } from '../ui/EmptyState';
import {
  StaleNotice,
  UnresolvedState,
  counted,
  refreshFailed,
  shownState,
  unresolved,
} from '../ui/QueryStates';
import { Button } from '../ui/Button';
import { IconFleet, IconPlus } from '../icons';
import { ASSET_STATUS, display } from '../status';
import { ASSET_KIND_LABEL } from './assetVocab';
import { MaintenanceDueTable, type DueRow } from './MaintenanceDueTable';
import { NewAssetSheet } from './AssetSheet';

type AssetRow = {
  id: string;
  kind: string;
  name: string;
  status: string;
  plate: string | null;
  make: string | null;
  model: string | null;
  serialNo: string | null;
  category: string | null;
  homeBaseAddress: string | null;
  assignedTo: { userId: string; name: string } | null;
};

/**
 * ⚠️ `filterable` is set on exactly the keys `ASSET_FILTER` declares in
 * `services/asset.ts`, and the sort keys match `ASSET_SORT`. In server mode an input the
 * service silently ignores looks editable while doing nothing — Sonar's own stated
 * anti-rule, and the class 103's no-op-sort census was about.
 *
 * `status` and `assigned` are deliberately NOT filterable: the screen says "Out of
 * service" where the row says `out_of_service`, and the holder is resolved after the
 * query rather than being a column SQL can match. Offering either would be a box that
 * takes typing and returns nothing.
 */
const assetColumns: DataTableColumn<AssetRow>[] = [
  {
    key: 'name',
    label: 'Name',
    filterable: true,
    value: (a) => a.name,
    cell: (a) => (
      <div>
        <p className="font-medium">{a.name}</p>
        {a.homeBaseAddress ? (
          <p className="text-xs text-muted-foreground">{a.homeBaseAddress}</p>
        ) : null}
      </div>
    ),
  },
  {
    key: 'kind',
    label: 'Kind',
    filterable: true,
    value: (a) => a.kind,
    cell: (a) => (
      <StatusBadge tone={a.kind === 'vehicle' ? 'brand' : 'neutral'}>
        {ASSET_KIND_LABEL[a.kind] ?? a.kind}
      </StatusBadge>
    ),
  },
  {
    key: 'status',
    label: 'Status',
    value: (a) => a.status,
    cell: (a) => {
      const s = display(ASSET_STATUS, a.status);
      return <StatusBadge tone={s.tone}>{s.label}</StatusBadge>;
    },
  },
  {
    key: 'plate',
    label: 'Plate',
    filterable: true,
    value: (a) => a.plate,
    cell: (a) => dash(a.plate),
  },
  {
    key: 'make',
    label: 'Make / model',
    filterable: true,
    value: (a) => a.make,
    // The cell prints both and the server matches either — search the same, or typing a
    // model finds nothing on the one page whose job is finding a van.
    searchValue: (a) => `${a.make ?? ''} ${a.model ?? ''}`,
    cell: (a) => dash([a.make, a.model].filter(Boolean).join(' ')),
  },
  {
    key: 'assigned',
    label: 'Assigned to',
    // Resolved after the page query (two queries, never a fan-out join), so there is no
    // column for SQL to sort on. An unsortable header beats one that cycles and does
    // nothing (the 103 census).
    cell: (a) => dash(a.assignedTo?.name),
  },
  {
    key: 'serial',
    label: 'Serial',
    filterable: true,
    defaultHidden: true,
    value: (a) => a.serialNo,
    cell: (a) => dash(a.serialNo),
  },
  {
    key: 'category',
    label: 'Category',
    filterable: true,
    defaultHidden: true,
    value: (a) => a.category,
    cell: (a) => dash(a.category),
  },
];

/**
 * The register (107): what the shop owns, and the two things it owes on them.
 *
 * Three tabs on one page rather than three routes — the due tables are views OF the
 * register, and A-2 made horizontal secondary nav the sanctioned density tool. The tab
 * rides in the URL (105), so 112's fleet-overview cards have a "View all" address to
 * point at.
 */
export function AssetsPage() {
  const trpc = useTRPC();
  const navigate = useNavigate();
  const [tab, setTab] = useUrlTab(ASSET_TABS);
  const [includeRetired, setIncludeRetired] = useState(false);
  // `includeRetired` narrows the result set from OUTSIDE the hook, so it joins the
  // hook's reset key — otherwise un-ticking it on page 2 asks for rows the new set
  // doesn't have and the footer reads "0 of 12" for a render.
  const { search, setSearch, query, table } = useServerTable(undefined, includeRetired);
  const [dueSearch, setDueSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [preview, setPreview] = useState<AssetRow | null>(null);

  const assetsQuery = useQuery({
    ...trpc.asset.list.queryOptions({
      ...query,
      ...(includeRetired ? { includeRetired: true } : {}),
    }),
    // Hold the previous page while the next loads, so typing doesn't flash the table.
    placeholderData: (prev) => prev,
  });
  const dueQuery = useQuery(trpc.asset.maintenanceDue.queryOptions());

  const openFull = (id: string) =>
    navigate({ to: '/fleet/assets/$assetId', params: { assetId: id } });

  // `shownState`, not `queryState`: a failed BACKGROUND refresh of a page already on
  // screen is no reason to replace the register with an error — the honesty is in
  // `refreshFailed`'s territory, not in blanking correct content (042).
  const assetsState = shownState(assetsQuery, assetsQuery.data);
  const dueState = shownState(dueQuery, dueQuery.data);
  const due: DueRow[] = dueQuery.data ?? [];
  const maintenance = due.filter((d) => d.card === 'maintenance');
  const reminders = due.filter((d) => d.card === 'reminder');

  return (
    <PageRoot>
      <PageHeader
        title="Fleet"
        sub="The vans and tools the shop owns — and what they're owed."
        right={
          <Button onClick={() => setCreating(true)}>
            <IconPlus size={15} />
            New asset
          </Button>
        }
      />

      <UnderlineTabs value={tab} onValueChange={setTab}>
        <UnderlineTabStrip
          tabs={[
            { key: 'assets', label: 'Assets' },
            // A count is a CLAIM — `counted` is the only way to make one, so a tab never
            // reads "Maintenance (0)" over a query that hasn't answered.
            {
              key: 'maintenance',
              label: counted('Maintenance', dueState, maintenance.length),
            },
            { key: 'reminders', label: counted('Reminders', dueState, reminders.length) },
          ]}
        />

        <Toolbar>
          {tab === 'assets' ? (
            <>
              <SearchInput
                placeholder="Search assets…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search assets"
              />
              <label className="flex items-center gap-2 text-sm text-muted-foreground">
                <input
                  type="checkbox"
                  checked={includeRetired}
                  onChange={(e) => setIncludeRetired(e.target.checked)}
                />
                Show retired
              </label>
            </>
          ) : (
            <SearchInput
              placeholder="Search what's due…"
              value={dueSearch}
              onChange={(e) => setDueSearch(e.target.value)}
              aria-label="Search what's due"
            />
          )}
        </Toolbar>

        {/*
         * ⭐ `keepMounted` on all three (105's law). The two due tables are CLIENT-mode
         * DataTables, so their sort, per-column filters and page live inside the
         * component — a glance at another tab would throw away a filtered view and
         * hand back the unfiltered one, which is the data-loss class 105 found on
         * JobDetail one noun over.
         */}
        <UnderlineTabPanel value="assets" keepMounted className="mt-4">
          {!assetsQuery.data ? (
            <UnresolvedState
              state={unresolved(assetsState)}
              what="your assets"
              icon={IconFleet}
              error={assetsQuery.error}
              onRetry={() => void assetsQuery.refetch()}
              skeleton="h-64"
            />
          ) : (
            <>
              {/* The register on screen is real and the last refresh failed — the count
                  in the footer is from the last time it loaded, and saying nothing here
                  would let a shop read it as current. */}
              <StaleNotice
                show={refreshFailed(assetsQuery, assetsQuery.data)}
                onRetry={() => void assetsQuery.refetch()}
              />
              <DataTable
                columns={assetColumns}
                rows={assetsQuery.data.rows}
                rowKey={(a) => a.id}
                search={search}
                tableId="assets"
                server={table(assetsQuery.data.total, assetsQuery.isFetching)}
                onRowClick={(a) => setPreview(a)}
                rowLabel={(a) => `Preview ${a.name}`}
                empty={
                  /*
                   * ⭐ Two empties, and the difference is which one can be TOLD the truth.
                   * With retired rows included, nothing found means nothing exists. Without
                   * them it means one of two things — an empty shop, or a shop whose whole
                   * register is retired — and this screen cannot tell which. So the copy
                   * says what is true either way and names the switch, rather than asserting
                   * "everything you've added is retired" at a shop that has added nothing.
                   * (The search/filter empty is DataTable's own, one branch further in.)
                   */
                  <EmptyState
                    icon={IconFleet}
                    title={
                      includeRetired
                        ? "You haven't added any assets yet"
                        : 'Nothing on the working register'
                    }
                    description={
                      includeRetired
                        ? 'Add the vans and tools the shop owns, and their maintenance lives here with them.'
                        : 'Add the vans and tools the shop owns. If what you’re after was retired, tick “Show retired”.'
                    }
                    action={
                      <Button onClick={() => setCreating(true)}>
                        <IconPlus size={15} />
                        New asset
                      </Button>
                    }
                  />
                }
              />
            </>
          )}
        </UnderlineTabPanel>

        <UnderlineTabPanel value="maintenance" keepMounted className="mt-4">
          {!dueQuery.data ? (
            <UnresolvedState
              state={unresolved(dueState)}
              what="maintenance due"
              icon={IconFleet}
              error={dueQuery.error}
              onRetry={() => void dueQuery.refetch()}
              skeleton="h-64"
            />
          ) : (
            <>
              {/* Both due tabs draw from this one query, so both say when it went stale —
                  a day count that failed to refresh is the kind of number somebody acts
                  on without checking. */}
              <StaleNotice
                show={refreshFailed(dueQuery, dueQuery.data)}
                onRetry={() => void dueQuery.refetch()}
              />
              <MaintenanceDueTable
                rows={maintenance}
                tableId="asset-maintenance"
                search={dueSearch}
                emptyTitle="Nothing owed on the vans"
                emptyDescription="Schedule a service from an asset's page and it shows up here until it's done."
              />
            </>
          )}
        </UnderlineTabPanel>

        <UnderlineTabPanel value="reminders" keepMounted className="mt-4">
          {!dueQuery.data ? (
            <UnresolvedState
              state={unresolved(dueState)}
              what="reminders due"
              icon={IconFleet}
              error={dueQuery.error}
              onRetry={() => void dueQuery.refetch()}
              skeleton="h-64"
            />
          ) : (
            <>
              <StaleNotice
                show={refreshFailed(dueQuery, dueQuery.data)}
                onRetry={() => void dueQuery.refetch()}
              />
              <MaintenanceDueTable
                rows={reminders}
                tableId="asset-reminders"
                search={dueSearch}
                emptyTitle="No paperwork coming up"
                emptyDescription="Insurance, registration and inspection dates go on the asset's page — they appear here as they come due."
              />
            </>
          )}
        </UnderlineTabPanel>
      </UnderlineTabs>

      {/*
       * Row click → preview → full page, the two-step every list in this app uses
       * (Customers, Jobs, Quotes). Scanning a register of sixty vans for the one with the
       * lapsed plates should not cost a page navigation and a Back per van.
       */}
      <Sheet
        open={preview !== null}
        onClose={() => setPreview(null)}
        title={preview?.name ?? ''}
        badge={
          preview ? (
            <StatusBadge tone={display(ASSET_STATUS, preview.status).tone}>
              {display(ASSET_STATUS, preview.status).label}
            </StatusBadge>
          ) : null
        }
        footer={preview ? <SheetOpenFull onClick={() => openFull(preview.id)} /> : null}
      >
        {preview ? (
          <dl className="space-y-3 text-sm">
            {(
              [
                ['Kind', ASSET_KIND_LABEL[preview.kind] ?? preview.kind],
                ...(preview.kind === 'vehicle'
                  ? ([
                      ['Plate', preview.plate],
                      [
                        'Make / model',
                        [preview.make, preview.model].filter(Boolean).join(' '),
                      ],
                      ['Home base', preview.homeBaseAddress],
                    ] as const)
                  : ([
                      ['Serial', preview.serialNo],
                      ['Category', preview.category],
                    ] as const)),
                ['Assigned to', preview.assignedTo?.name ?? null],
              ] as const
            ).map(([label, value]) => (
              <div key={label} className="flex items-baseline justify-between gap-4">
                <dt className="text-muted-foreground">{label}</dt>
                <dd className="text-right font-medium">{dash(value)}</dd>
              </div>
            ))}
          </dl>
        ) : null}
      </Sheet>
      <NewAssetSheet
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={openFull}
      />
    </PageRoot>
  );
}
